import React, { useState } from 'react'
import classNames from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload'
import { faUpload } from '@fortawesome/pro-regular-svg-icons/faUpload'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'

import { GET_LIST, useClient } from '@peracto/client'
import { useConfig } from '@peracto/peracto-config'
import { Link } from 'react-router-dom'
import { HydraTable, Heading, TextFilter, SelectFilter, StyledCheckbox } from '@peracto/peracto-ui'
import { Helmet } from 'react-helmet'

const statusValues = {
    active: 'Active',
    inactive: 'Inactive',
}

const statusOptions = () => {
    const opts = []
    for (const [key, val] of Object.entries(statusValues)) {
        opts.push({ label: val, value: key })
    }
    return opts
}

const LocationsList = () => {
    const { client } = useClient()
    const [selectedColumns, onSelectColumn] = useState({
        postcode: false,
        website: false,
        country: false,
        county: false,
        email: false,
    })

    const config = useConfig()
    const { locations } = config.get('features', {})

    const fields = [
        {
            header: 'Type',
            id: 'locationTypes.type',
            accessor: 'locationTypes',
            show: true,
            selectable: false,
            Cell: ({ cell: { value } }) => {
                const values = value
                    .filter(v => v?.type)
                    .map(val => {
                        return val.type.replace(/_/g, ' ')
                    })

                return <span className="!capitalize">{values.join(', ')}</span>
            },
        },
        {
            header: 'Status',
            accessor: 'locationPageStatus',
            show: true,
            selectable: false,
            Cell: ({ cell: { value } }) => (
                <span
                    className={classNames('badge', 'badge-pill', {
                        'badge-success': value == 'active',
                        'badge-danger': value == 'inactive',
                    })}
                >
                    {statusValues[value]}
                </span>
            ),
            Filter: props => <SelectFilter label="Status" options={statusOptions()} {...props} />,
        },
        {
            header: 'Website',
            accessor: 'website',
            show: selectedColumns['website'],
            selectable: true,
        },
        {
            header: 'Email',
            accessor: 'email',
            show: selectedColumns['email'],
            selectable: true,
        },
        {
            header: 'County',
            accessor: 'county',
            show: selectedColumns['county'],
            selectable: true,
        },
        {
            header: 'Postal Code',
            accessor: 'postcode',
            show: selectedColumns['postcode'],
            selectable: true,
        },
        {
            header: 'Country',
            accessor: 'country',
            show: selectedColumns['country'],
            selectable: true,
        },
    ]

    const columns = [
        {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => (
                <StyledCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            Cell: ({ row }) => <StyledCheckbox {...row.getToggleRowSelectedProps()} />,
            selectable: false,
        },
        {
            Cell: row => <Link to={row.row.original.id}>{row.cell.value}</Link>,
            Filter: props => <TextFilter label="Name" {...props} />,
            sortType: 'basic',
            selectable: false,
            accessor: 'name',
            Header: 'Name',
        },
    ]

    fields.forEach(({ header, accessor, selectable, show, id = null, Cell, Filter }) =>
        columns.push({
            Header: header,
            accessor: accessor,
            sortType: 'basic',
            Filter: Filter ? Filter : props => <TextFilter label={header} {...props} />,
            Cell: Cell ? Cell : ({ cell: { value } }) => (value ? value : '-'),
            selectable: selectable,
            show: show,
            id,
        })
    )

    const onFetchData = options => client(GET_LIST, 'locations', options)

    return (
        <>
            <Helmet>
                <title>Locations | Peracto</title>
            </Helmet>
            <Heading name="Locations">
                <div className="!flex !items-center !ml-2">
                    {locations?.exportLocationData !== false && (
                        <Link
                            className="btn btn-link !mr-2"
                            to="/locations/export"
                            data-testid="export-locations"
                        >
                            <FontAwesomeIcon icon={faDownload} className="!mr-2" />
                            Export Location Data
                        </Link>
                    )}

                    <Link className="btn btn-outline-primary !mr-2" to="/locations/import">
                        <FontAwesomeIcon icon={faUpload} className="!mr-2" />
                        Import Location Data
                    </Link>

                    <Link
                        className="btn btn-primary"
                        to="/locations/add"
                        data-testid="add-location"
                    >
                        <FontAwesomeIcon icon={faPlusCircle} className="!mr-2" />
                        Add Location
                    </Link>
                </div>
            </Heading>

            <div className="row">
                <div className="col">
                    <div className="card card--transparent">
                        <div className="card-body">
                            <HydraTable
                                columns={columns}
                                selectedColumns={selectedColumns}
                                onFetchData={onFetchData}
                                onSelectColumn={onSelectColumn}
                                defaultFilters={{
                                    status: 'active',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default LocationsList
